import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Heading,
  Image,
  Text,
  Stack,
  Box,
  HStack,
  Tag,
  TagLabel,
  useColorModeValue,
  Badge,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { IProject } from "./Project";

interface ProjectCardProps {
  project: IProject;
}

export const ProjectCard = ({ project }: ProjectCardProps): JSX.Element => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");
  const hoverShadow = useColorModeValue(
    "0 12px 28px -8px rgba(0,0,0,0.12)",
    "0 12px 28px -8px rgba(0,0,0,0.4)"
  );
  const imageBg = useColorModeValue("gray.100", "gray.700");

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      h="full"
      transition="all 0.2s ease"
      _hover={{
        boxShadow: hoverShadow,
        borderColor: "accent.400",
      }}
      cursor="pointer"
      onClick={() => window.open(project.url, "_blank")}
    >
      <Box position="relative" h="180px" overflow="hidden" bg={imageBg}>
        <Image
          src={`/img/projects/${project.image}`}
          alt={project.title}
          objectFit="cover"
          w="full"
          h="full"
          transition="transform 0.3s ease"
          _hover={{ transform: "scale(1.03)" }}
        />
        <Badge
          position="absolute"
          top={3}
          right={3}
          bg="accent.500"
          color="white"
          fontSize="xs"
          fontWeight="600"
          borderRadius="full"
          px={3}
          py={1}
        >
          {project.year}
        </Badge>
      </Box>

      <VStack align="stretch" flex={1} spacing={0}>
        <CardHeader pb={2}>
          <Flex align="center" justify="space-between" gap={2}>
            <Heading size="sm" fontWeight="600" color={headingColor} lineHeight="1.3">
              {project.title}
            </Heading>
            <Text fontSize="sm" color={textColor} opacity={0.8}>
              ↗
            </Text>
          </Flex>
        </CardHeader>

        <CardBody pt={0} flex="1">
          <Stack spacing={2}>
            {project.description.map((line, index) => (
              <Text key={index} fontSize="sm" color={textColor} lineHeight="1.6">
                {line}
              </Text>
            ))}
          </Stack>
        </CardBody>

        <CardFooter pt={0} pb={4}>
          <HStack spacing={2} flexWrap="wrap" gap={1}>
            {project.technologies.map((tech, index) => (
              <Tag
                key={index}
                size="sm"
                variant="subtle"
                colorScheme="teal"
                borderRadius="md"
                fontWeight="500"
              >
                <TagLabel>{tech}</TagLabel>
              </Tag>
            ))}
          </HStack>
        </CardFooter>
      </VStack>
    </Card>
  );
};
